import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { useLang, MAPS_URL } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const COAST_IMG =
  "https://images.pexels.com/photos/16413831/pexels-photo-16413831.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Location() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="posizione" ref={ref} className="border-t border-line bg-sand2 py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <Reveal>
            <Eyebrow>{t.location.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-serif text-4xl font-light leading-[1.05] md:text-6xl">
              {t.location.title}
              <br />
              <span className="italic text-olive">{t.location.sub}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-ink2 md:text-lg">
              {t.location.p}
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-8 flex items-center gap-3 text-sm font-medium text-ink">
              <MapPin className="h-4 w-4 text-terra" />
              {t.location.address}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-maps-link"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-sand transition-all duration-300 hover:bg-terra"
            >
              {t.location.maps}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal y={40}>
            <div className="relative overflow-hidden rounded-lg" data-testid="location-image">
              <motion.img
                src={COAST_IMG}
                alt="La costa siciliana al tramonto"
                style={{ y, scale: 1.12 }}
                className="h-[380px] w-full object-cover md:h-[560px]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent" />
              <span className="absolute bottom-5 left-5 rounded-full bg-sand/85 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink backdrop-blur-md">
                {t.location.chip}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
